import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  MapPin, Users, Bed, Bath, Star, Heart, Zap,
  ArrowLeft, Check, Calendar, Share2, MessageSquare, X, Send,
  CreditCard, Clock, AlertTriangle, Info
} from 'lucide-react';
import { propertyAPI, bookingAPI, calendarAPI, messagesAPI, paymentsAPI, reviewsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Badge } from '../components/ui/badge';
import VerificationBadge from '../components/VerificationBadge';
import usePageMeta from '../hooks/usePageMeta';
import useCrawlerNotFound from '../seo/useCrawlerNotFound';

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthToken, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [propertyMissing, setPropertyMissing] = useState(false); // API said 400/404
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [booking, setBooking] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [specialRequests, setSpecialRequests] = useState('');
  const [contactingHost, setContactingHost] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Earliest selectable check-in date as YYYY-MM-DD in local time. After
  // 23:00 local the backend refuses same-day bookings (see bookings.py
  // late_night_wall), so the picker should grey today out natively.
  // Computed once per render; cheap.
  const getEarliestCheckIn = () => {
    const now = new Date();
    if (now.getHours() >= 23) {
      now.setDate(now.getDate() + 1);
    }
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Booking form state — pre-fill from URL query params if available.
  // If a URL param tries to pre-fill today's date after 23:00, we ignore
  // it on mount rather than letting the user submit a date the backend
  // will reject. Better than a confusing toast a moment later.
  const _urlCheckIn = searchParams.get('checkIn') || '';
  const _earliest = getEarliestCheckIn();
  const _safeCheckIn = _urlCheckIn && _urlCheckIn >= _earliest ? _urlCheckIn : '';
  const _urlCheckOut = searchParams.get('checkOut') || '';
  const _safeCheckOut = _urlCheckOut && _safeCheckIn && _urlCheckOut > _safeCheckIn ? _urlCheckOut : '';
  const [checkIn, setCheckIn] = useState(_safeCheckIn);
  const [checkOut, setCheckOut] = useState(_safeCheckOut);
  const [guests, setGuests] = useState(1);
  const [effectivePricing, setEffectivePricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // Booking confirmation modal state
  const [showBookingConfirmModal, setShowBookingConfirmModal] = useState(false);
  const [bookingOptions, setBookingOptions] = useState(null);
  const [bookingOptionsLoading, setBookingOptionsLoading] = useState(false);

  // Per-property tab title + social share tags (Open Graph / Twitter). Feeds
  // the browser tab now and the build-time prerender for crawlers. Falls back
  // to the site-wide defaults in index.html until the property has loaded.
  const _shareImg = property?.images?.[0];
  usePageMeta({
    title: propertyMissing
      ? 'Listing not found | Ariv'
      : (property?.title ? `${property.title} · Ariv` : undefined),
    description: property?.description
      ? property.description.replace(/\s+/g, ' ').trim().slice(0, 200)
      : undefined,
    image: _shareImg
      ? (/^https?:\/\//.test(_shareImg)
          ? _shareImg
          : `https://ariv.co.uk${_shareImg.startsWith('/') ? '' : '/'}${_shareImg}`)
      : undefined,
    imageAlt: property?.title,
    url: `https://ariv.co.uk/property/${propertyId}`,
    // A listing that loaded is public. Left undefined when it is missing, so
    // useCrawlerNotFound below is the one that speaks and marks it noindex.
    robots: propertyMissing ? undefined : 'index, follow, max-image-preview:large',
    type: 'website',
  });

  // A listing that doesn't exist (bad or deleted ID) must not look like a
  // real page to crawlers: noindex, plus a prerender-status-code tag that will
  // give a real 404 again if a prerendering proxy returns (removed 2026-10-15). Set
  // only on a definite 400/404 from the API, never on a network or server
  // error, so an outage can't get real listings dropped.
  useCrawlerNotFound(propertyMissing);

  // Signal that the page is ready to snapshot once the property has
  // loaded (or failed), so crawlers capture the real title and photo instead
  // of the loading spinner. Runs after usePageMeta has applied the tags.
  useEffect(() => {
    if (typeof window !== 'undefined' && (property || error)) {
      window.prerenderReady = true;
    }
  }, [property, error]);

  useEffect(() => {
    fetchProperty();
    fetchAvailability();
    fetchReviews();
  }, [propertyId]);

  // Fetch effective pricing when dates change
  useEffect(() => {
    const fetchEffectivePricing = async () => {
      if (checkIn && checkOut && propertyId) {
        setPricingLoading(true);
        try {
          const pricing = await calendarAPI.getEffectivePrice(propertyId, checkIn, checkOut);
          setEffectivePricing(pricing);
        } catch (err) {
          console.error('Error fetching effective pricing:', err);
          setEffectivePricing(null);
        } finally {
          setPricingLoading(false);
        }
      } else {
        setEffectivePricing(null);
      }
    };
    fetchEffectivePricing();
  }, [checkIn, checkOut, propertyId]);

  const fetchProperty = async () => {
    setLoading(true);
    setPropertyMissing(false);
    try {
      const data = await propertyAPI.getProperty(propertyId);
      setProperty(data);
    } catch (err) {
      console.error('Error fetching property:', err);
      const status = err?.response?.status;
      setPropertyMissing(status === 404 || status === 400);
      setError('Property not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const data = await calendarAPI.getAvailability(propertyId);
      setUnavailableDates(data.unavailableDates || []);
    } catch (err) {
      console.error('Error fetching availability:', err);
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const data = await reviewsAPI.getPropertyReviews(propertyId);
      setReviews(data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Step 1: "Book Now" button opens the confirmation modal
  const handleBooking = async () => {
    if (!isAuthenticated()) {
      toast({ title: 'Login Required', description: 'Please login to book this property', variant: 'destructive' });
      navigate('/login');
      return;
    }

    if (!checkIn || !checkOut) {
      toast({ title: 'Error', description: 'Please select check-in and check-out dates', variant: 'destructive' });
      return;
    }

    // Fetch booking options (deadlines, modes)
    setBookingOptionsLoading(true);
    try {
      const options = await bookingAPI.getBookingOptions(propertyId, checkIn, checkOut);
      setBookingOptions(options);

      // Three same-day windows now (see backend get_booking_options):
      //   - allowed normally (before host's check-in time)
      //   - requires host approval (between check-in time and 23:00)
      //   - hard-blocked (after 23:00 or any other "not allowed" case)
      if (options.isSameDay && !options.sameDayAllowed) {
        const message = options.sameDayBlockedLateNight
          ? `It's after 23:00. Same-day bookings aren't available — please pick tomorrow.`
          : `Same-day bookings must be made before ${options.checkInTime || '15:00'}. Please select a later date.`;
        toast({
          title: 'Same-Day Booking Unavailable',
          description: message,
          variant: 'destructive',
        });
        return;
      }

      // Approval window opens the modal but the modal will show a single
      // "Request Stay" CTA instead of Pay Now / Reserve Later (handled
      // inside the modal via options.requiresHostApproval).
      setShowBookingConfirmModal(true);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Unable to check booking options. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setBookingOptionsLoading(false);
    }
  };

  // Step 2: Guest chooses "Pay Now" or "Reserve Later"
  const handleConfirmBooking = async (mode) => {
    setBooking(true);
    setShowBookingConfirmModal(false);
    try {
      const token = getAuthToken();
      const bookingData = {
        propertyId,
        checkIn,
        checkOut,
        numberOfGuests: guests,
        specialRequests,
        bookingMode: mode,
        addOns: { ride: false, food: false }
      };

      // Create booking with chosen mode
      const result = await bookingAPI.createBooking(bookingData, token);

      // Host-approval flow: booking lands in pending_host_approval. No
      // Stripe session yet — the host accepts first, then the guest gets
      // an email with a payment link.
      if (result.requiresHostApproval || result.status === 'pending_host_approval') {
        toast({
          title: 'Request sent to host',
          description: "We've let the host know. You'll get an email if they accept (usually within an hour or two).",
        });
        navigate('/guest/browse?tab=upcoming');
        return;
      }

      if (mode === 'pay_now') {
        // Try Stripe checkout first, fall back to test payment
        try {
          const checkoutData = await paymentsAPI.createCheckoutSession(
            result._id,
            window.location.origin,
            token
          );
          if (checkoutData.checkoutUrl) {
            toast({
              title: 'Redirecting to Payment',
              description: 'You will be redirected to complete payment securely.'
            });
            window.location.href = checkoutData.checkoutUrl;
            return;
          }
          throw new Error('No checkout URL received');
        } catch (paymentError) {
          console.log('Stripe not available, using test payment mode');
          try {
            const testResult = await paymentsAPI.testCheckout(result._id, token);
            if (testResult.success) {
              toast({
                title: 'Booking Confirmed!',
                description: 'Your booking has been confirmed. (Test mode - no payment charged)'
              });
              navigate('/guest/browse?tab=upcoming');
            } else {
              throw new Error('Test payment failed');
            }
          } catch (testError) {
            console.error('Test payment also failed:', testError);
            toast({
              title: 'Booking Created',
              description: 'Booking created. Complete payment from your dashboard.'
            });
            navigate('/guest/browse?tab=upcoming');
          }
        }
      } else {
        // Reserve Later — booking is created with pending_payment status + deadline
        const deadlineDate = result.paymentDeadline
          ? new Date(result.paymentDeadline).toLocaleDateString('en-GB', {
              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })
          : 'before check-in';

        toast({
          title: 'Reservation Confirmed!',
          description: `Your dates are reserved. Please complete payment by ${deadlineDate} to secure your booking.`
        });
        navigate('/guest/browse?tab=upcoming');
      }
    } catch (err) {
      toast({
        title: 'Booking Failed',
        description: err.response?.data?.detail || 'Unable to complete booking. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setBooking(false);
    }
  };

  const handleContactHost = async () => {
    if (!isAuthenticated()) {
      toast({ title: 'Login Required', description: 'Please login to contact the host', variant: 'destructive' });
      navigate('/login');
      return;
    }

    // Show modal for inquiry message
    setShowContactModal(true);
  };

  const handleSendInquiry = async () => {
    if (!inquiryMessage.trim()) {
      toast({ title: 'Error', description: 'Please enter a message', variant: 'destructive' });
      return;
    }

    setContactingHost(true);
    try {
      const token = getAuthToken();
      const conv = await messagesAPI.createConversation({
        propertyId,
        isInquiry: true,
        initialMessage: inquiryMessage.trim()
      }, token);

      toast({ title: 'Message Sent!', description: 'The host will respond to your inquiry.' });
      setShowContactModal(false);
      setInquiryMessage('');

      // Navigate to guest messages with conversation auto-selected
      const convId = conv._id || conv.id;
      navigate(`/guest/browse?tab=messages${convId ? `&conversationId=${convId}` : ''}`);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to send message', variant: 'destructive' });
    } finally {
      setContactingHost(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Property Not Found</h2>
          <Button onClick={() => navigate('/')}>Back to Browse</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft size={20} />
              </Button>
              <Link to="/" className="flex items-center gap-3">
                <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setIsFavorite(!isFavorite)}>
                <Heart 
                  size={20} 
                  className={isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'} 
                />
              </Button>
              <Button variant="ghost">
                <Share2 size={20} />
              </Button>
            </div>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Title Section */}
        <div className="mb-6">
          <h1 className="text-xl md:text-3xl font-bold text-[#1F3A68] mb-2">{property.title}</h1>
          <div className="flex items-center gap-4 text-gray-600 flex-wrap">
            {property.rating > 0 && (
              <span className="flex items-center gap-1">
                <Star size={16} fill="#1F3A68" className="text-[#1F3A68]" />
                <span className="font-semibold">{property.rating}</span>
                <span>({property.reviewCount} reviews)</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <MapPin size={16} />
              {property.location?.city}, {property.location?.country}
            </span>
            {property.verificationStatus && (
              <VerificationBadge status={property.verificationStatus} size="md" />
            )}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Ariv is a marketplace that connects you with independent hosts. Your host, not Ariv, is responsible for the property and your stay.
          </p>
        </div>

        {/* Image Gallery */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="relative rounded-xl overflow-hidden h-56 md:h-96">
            <img
              src={property.images?.[selectedImage] || 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&h=800&fit=crop'}
              alt={property.title}
              className="w-full h-full object-cover"
            />
            {property.instantBook && (
              <div className="absolute top-4 left-4 bg-[#1F3A68] text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                <Zap size={14} />
                Instant Book
              </div>
            )}
          </div>
          {property.images?.length > 1 && (
            <div className="grid grid-cols-2 gap-4">
              {property.images.slice(1, 5).map((image, index) => (
                <div 
                  key={index}
                  className="relative rounded-xl overflow-hidden h-44 cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setSelectedImage(index + 1)}
                >
                  <img
                    src={image}
                    alt={`${property.title} ${index + 2}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-4 md:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Host & Quick Info */}
            <div className="flex items-center justify-between border-b pb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {property.propertyType} hosted by {property.host?.name || 'Host'}
                </h2>
                <div className="flex items-center gap-4 text-gray-600 mt-2">
                  <span className="flex items-center gap-1">
                    <Users size={16} />
                    {property.capacity?.guests} guests
                  </span>
                  <span className="flex items-center gap-1">
                    <Bed size={16} />
                    {property.capacity?.bedrooms} bedrooms
                  </span>
                  <span className="flex items-center gap-1">
                    <Bath size={16} />
                    {property.capacity?.bathrooms} bathrooms
                  </span>
                </div>
              </div>
              {property.host?.verified && (
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#1F3A68] rounded-full flex items-center justify-center text-white mb-1">
                    <Check size={24} />
                  </div>
                  <span className="text-xs text-gray-600">Identity verified</span>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold mb-3">About this place</h3>
              <p className="text-gray-600 leading-relaxed">{property.description}</p>
            </div>

            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">What this place offers</h3>
                <div className="grid grid-cols-2 gap-4">
                  {property.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check size={20} className="text-[#1F3A68]" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* House Rules */}
            {property.houseRules && (
              <div>
                <h3 className="text-lg font-semibold mb-3">House Rules</h3>
                <p className="text-gray-600">{property.houseRules}</p>
              </div>
            )}

            {/* Check-in/out Times */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold mb-1">Check-in</h4>
                <p className="text-gray-600">{property.checkInTime || '15:00'}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold mb-1">Check-out</h4>
                <p className="text-gray-600">{property.checkOutTime || '11:00'}</p>
              </div>
            </div>

            {/* Reviews Section */}
            <div id="reviews">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Star size={20} fill="#1F3A68" className="text-[#1F3A68]" />
                  {property.rating > 0 ? (
                    <span>{property.rating} · {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
                  ) : (
                    <span>Reviews</span>
                  )}
                </h3>
              </div>

              {reviewsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1F3A68] mx-auto"></div>
                </div>
              ) : reviews.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <Star size={32} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">No reviews yet. Be the first to stay and review!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {reviews.map(review => (
                    <div key={review._id} className="border-b pb-6 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#1F3A68] rounded-full flex items-center justify-center text-white font-semibold">
                            {(review.guestName || 'G').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold">{review.guestName}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(review.createdAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star
                              key={star}
                              size={16}
                              className={star <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}
                            />
                          ))}
                        </div>
                      </div>
                      {review.title && (
                        <p className="font-medium mb-1">{review.title}</p>
                      )}
                      <p className="text-gray-600">{review.comment}</p>
                      {review.hostReply && (
                        <div className="mt-3 ml-6 bg-gray-50 rounded-lg p-3">
                          <p className="text-sm font-semibold text-[#1F3A68] mb-1">
                            Response from {property.host?.name || 'Host'}
                          </p>
                          <p className="text-sm text-gray-600">{review.hostReply}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-[#1F3A68]">
                    £{property.pricing?.nightlyRate || 0}
                  </span>
                  <span className="text-gray-600 text-base font-normal">/ night</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1">Check-in</label>
                    <Input
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      // After 23:00 local time, today is no longer a valid
                      // check-in (backend refuses, see bookings.py same-day
                      // late-night wall). Bumping the picker's `min` to
                      // tomorrow greys today out natively in the browser
                      // so users can't pick it. We keep the toast error
                      // path as a fallback in case anyone bypasses the
                      // picker (manual typing, programmatic, autofill).
                      min={getEarliestCheckIn()}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Check-out</label>
                    <Input
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      min={checkIn || getEarliestCheckIn()}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Guests</label>
                  <Input
                    type="number"
                    min="1"
                    max={property.capacity?.guests || 10}
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value))}
                  />
                </div>

                {pricingLoading && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1F3A68] mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Calculating price...</p>
                  </div>
                )}

                {effectivePricing && !pricingLoading && (
                  <div className="border-t pt-4 space-y-2">
                    {effectivePricing.hasOverrides ? (
                      <>
                        <p className="text-xs text-violet-600 font-medium mb-2">
                          * Price includes special rates for selected dates
                        </p>
                        <div className="flex justify-between">
                          <span>Accommodation ({effectivePricing.nights} nights)</span>
                          <span>£{effectivePricing.accommodation.toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between">
                        <span>£{effectivePricing.baseRate} x {effectivePricing.nights} nights</span>
                        <span>£{effectivePricing.accommodation.toFixed(2)}</span>
                      </div>
                    )}
                    {effectivePricing.cleaningFee > 0 && (
                      <div className="flex justify-between">
                        <span>Cleaning fee</span>
                        <span>£{effectivePricing.cleaningFee.toFixed(2)}</span>
                      </div>
                    )}
                    {effectivePricing.serviceFee > 0 && (
                      <div className="flex justify-between">
                        <span>Service fee</span>
                        <span>£{effectivePricing.serviceFee.toFixed(2)}</span>
                      </div>
                    )}
                    {(effectivePricing.vat != null && effectivePricing.vat > 0) && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>VAT ({effectivePricing.vatRate || 20}%)</span>
                        <span>£{(effectivePricing.vat || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total</span>
                      <span>£{effectivePricing.total.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <Button
                  className="w-full bg-[#CB3B3B] hover:bg-[#CB3B3B]/90 h-12 text-lg"
                  disabled={!effectivePricing || pricingLoading || booking || bookingOptionsLoading}
                  onClick={handleBooking}
                  data-testid="book-now-btn"
                >
                  {booking ? 'Processing...' : bookingOptionsLoading ? 'Checking...' : 'Reserve'}
                </Button>

                <p className="text-center text-sm text-gray-500">
                  Choose to pay now or reserve and pay later
                </p>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleContactHost}
                  data-testid="contact-host-btn"
                >
                  <MessageSquare size={16} className="mr-2" />
                  Contact Host
                </Button>

                {/* Cancellation policy — surface the headline next to the
                    booking widget so guests know the rules before they pay. */}
                {property?.cancellationPolicy && (() => {
                  const policy = property.cancellationPolicy;
                  const summaries = {
                    flexible:  { label: 'Flexible',           headline: 'Full refund up to 24 hours before check-in.' },
                    moderate:  { label: 'Moderate',           headline: 'Full refund up to 5 days before check-in.' },
                    firm:      { label: 'Firm',               headline: 'Full refund up to 30 days before check-in.' },
                    strict:    { label: 'Strict',             headline: '50% refund up to 7 days before check-in.' },
                    long_term: { label: 'Long-term',          headline: 'First month non-refundable, then 30-day notice.' },
                  };
                  const s = summaries[policy] || summaries.moderate;
                  return (
                    <div className="text-xs text-gray-600 pt-2 border-t">
                      <span className="font-semibold text-[#1F3A68]">{s.label} cancellation</span>
                      <span className="text-gray-500"> · {s.headline}</span>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Contact Host Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-[#1F3A68]">Contact {property?.host?.name || 'Host'}</DialogTitle>
            <DialogDescription>
              Send a message about {property?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Hi! I'm interested in your property and have a few questions..."
              value={inquiryMessage}
              onChange={(e) => setInquiryMessage(e.target.value)}
              className="min-h-[150px]"
              data-testid="inquiry-message-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendInquiry}
              disabled={contactingHost || !inquiryMessage.trim()}
              className="bg-[#1F3A68]"
              data-testid="send-inquiry-btn"
            >
              {contactingHost ? 'Sending...' : 'Send Message'}
              <Send size={16} className="ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Confirmation Modal — Pay Now / Reserve Later */}
      <Dialog open={showBookingConfirmModal} onOpenChange={setShowBookingConfirmModal}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-[#1F3A68] text-xl">Confirm Your Booking</DialogTitle>
            <DialogDescription>
              {property?.title} · {checkIn && checkOut && (
                <>
                  {new Date(checkIn).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  {' - '}
                  {new Date(checkOut).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {effectivePricing && ` · ${effectivePricing.nights} night${effectivePricing.nights !== 1 ? 's' : ''}`}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Pricing Summary */}
            {effectivePricing && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Accommodation ({effectivePricing.nights} nights)</span>
                  <span>£{effectivePricing.accommodation?.toFixed(2)}</span>
                </div>
                {effectivePricing.cleaningFee > 0 && (
                  <div className="flex justify-between">
                    <span>Cleaning fee</span>
                    <span>£{effectivePricing.cleaningFee?.toFixed(2)}</span>
                  </div>
                )}
                {effectivePricing.serviceFee > 0 && (
                  <div className="flex justify-between">
                    <span>Service fee</span>
                    <span>£{effectivePricing.serviceFee?.toFixed(2)}</span>
                  </div>
                )}
                {(effectivePricing.vat != null && effectivePricing.vat > 0) && (
                  <div className="flex justify-between text-gray-500">
                    <span>VAT ({effectivePricing.vatRate || 20}%)</span>
                    <span>£{effectivePricing.vat?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base border-t pt-2">
                  <span>Total</span>
                  <span>£{effectivePricing.total?.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Same-day info banner */}
            {bookingOptions?.isSameDay && !bookingOptions?.requiresHostApproval && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-800">Same-Day Booking</p>
                  <p className="text-amber-700">
                    Your check-in is today. Immediate payment is required for same-day bookings.
                  </p>
                </div>
              </div>
            )}

            {/* Host-approval banner — replaces Pay Now / Reserve Later when
                the request lands after the host's check-in time. */}
            {bookingOptions?.requiresHostApproval && (
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-blue-800">Host Approval Needed</p>
                  <p className="text-blue-700">
                    It's past the host's check-in time. We'll send your request
                    to the host — if they accept, you'll have a short window to pay.
                  </p>
                </div>
              </div>
            )}

            {/* Next-day info banner */}
            {bookingOptions?.isNextDay && !bookingOptions?.isSameDay && (
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-blue-800">Next-Day Booking</p>
                  <p className="text-blue-700">
                    Your check-in is tomorrow. If you choose to reserve now, your reservation will be held for 5 hours.
                  </p>
                </div>
              </div>
            )}

            {/* Pay Now / Request Stay — single CTA. When the request needs
                host approval, the same handler runs but the booking lands
                in pending_host_approval status and no Stripe session is
                created until the host accepts. */}
            <div
              onClick={() => handleConfirmBooking('pay_now')}
              className="border-2 border-[#CB3B3B] rounded-lg p-4 cursor-pointer hover:bg-red-50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#CB3B3B] rounded-full flex items-center justify-center">
                  <CreditCard size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-[#CB3B3B]">
                    {bookingOptions?.requiresHostApproval ? 'Request Stay' : 'Pay Now'}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {bookingOptions?.requiresHostApproval
                      ? 'Send a request to the host'
                      : 'Confirm your booking instantly'}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500 ml-13 pl-[52px]">
                {bookingOptions?.requiresHostApproval
                  ? "Your dates are held while the host reviews. If they accept within a couple of hours, you'll get an email with a payment link."
                  : 'Secure your dates with immediate payment. Your booking will be confirmed right away.'}
              </p>
            </div>

            {/* Reserve Later Option — hidden for last-minute AND for host-approval flow */}
            {bookingOptions?.reserveLaterAvailable && !bookingOptions?.requiresHostApproval && (
              <div
                onClick={() => handleConfirmBooking('reserve_later')}
                className="border-2 border-[#1F3A68] rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#1F3A68] rounded-full flex items-center justify-center">
                    <Calendar size={20} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1F3A68]">Reserve Now, Pay Later</h4>
                    <p className="text-sm text-gray-600">Hold your dates for {bookingOptions?.reserveHoldHours || 24} hours</p>
                  </div>
                </div>
                <div className="pl-[52px] space-y-1">
                  <p className="text-sm text-gray-500">
                    Your dates will be reserved for {bookingOptions?.reserveHoldHours || 24} hours. Complete payment from your dashboard before:
                  </p>
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#1F3A68]">
                    <Clock size={14} />
                    <span>
                      {bookingOptions?.paymentDeadline
                        ? new Date(bookingOptions.paymentDeadline).toLocaleDateString('en-GB', {
                            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })
                        : `${bookingOptions?.reserveHoldHours || 24} hours after reservation`}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">
                    If payment is not received by this deadline, your reservation will expire and the dates will become available for other guests.
                  </p>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-400 text-center px-2">
            Ariv is a marketplace that connects you with independent hosts. Your host, not Ariv, is responsible for the property and your stay.
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingConfirmModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetailPage;
